import cloneDeep from 'lodash/cloneDeep';

import { IBuildingProviderContext } from '../@types/ContextProviderRelated';
import { Side } from '../@types/Side';
import { IWall } from '../models/abstract/Wall';
import { IRadiator } from '../models/standard/RadiatorModel';
import { getAngleFromPoints } from './vectorUtils';
import { snapDoorToWall, snapPassageToWall, snapRadiatorToWall, snapWindowToWall } from './wallElementSnapping';

type WallWithLegacyRadiators = IWall & {
  radiators?: IRadiator[];
};

type BoundaryWithWallReferences = {
  walls?: IWall[];
  edges?: Array<{ wall?: IWall }>;
};

const snapRadiators = (wall: IWall, radiators: IRadiator[] | undefined, side: Side, angle: number) => {
  if (!Array.isArray(radiators)) return;

  radiators.forEach((radiator) => {
    radiator.position = snapRadiatorToWall(wall, radiator.position, radiator.length, radiator.depth, radiator.nicheDepth ?? 0, side);
    radiator.angle = angle;
  });
};

const relinkRoomWallReferences = (building: IBuildingProviderContext) => {
  const wallByUuid = new Map<string, IWall>();

  (building.walls ?? []).forEach((wall) => {
    if (wall?.uuid) wallByUuid.set(wall.uuid, wall as IWall);
  });

  (building.rooms ?? []).forEach((room) => {
    (room.spaces ?? []).forEach((space) => {
      space.walls = (space.walls ?? []).map((wall) => wallByUuid.get(wall?.uuid ?? '') ?? wall);

      (space.boundaries ?? []).forEach((boundary) => {
        const wallBoundary = boundary as BoundaryWithWallReferences;

        if (Array.isArray(wallBoundary.walls)) {
          wallBoundary.walls = wallBoundary.walls.map((wall) => wallByUuid.get(wall?.uuid ?? '') ?? wall);
        }

        if (Array.isArray(wallBoundary.edges)) {
          wallBoundary.edges.forEach((edge) => {
            const canonicalWall = wallByUuid.get(edge?.wall?.uuid ?? '');
            if (canonicalWall) edge.wall = canonicalWall;
          });
        }
      });
    });
  });
};

export const snapWallElementsToWalls = (building: IBuildingProviderContext): IBuildingProviderContext => {
  const snappedBuilding = cloneDeep(building);

  (snappedBuilding.walls ?? []).forEach((wall) => {
    const typedWall = wall as IWall;
    const angle = getAngleFromPoints(typedWall.startPoint, typedWall.endPoint);

    (typedWall.doors ?? []).forEach((door) => {
      door.position = snapDoorToWall(typedWall, door.position, door.length);
      door.angle = angle;
      door.depth = typedWall.depth;
    });

    (typedWall.windows ?? []).forEach((window) => {
      window.position = snapWindowToWall(typedWall, window.position, window.length);
      window.angle = angle;
      window.depth = typedWall.depth;
    });

    (typedWall.passages ?? []).forEach((passage) => {
      passage.position = snapPassageToWall(typedWall, passage.position, passage.length);
      passage.angle = angle;
      passage.depth = typedWall.depth;
    });

    snapRadiators(typedWall, typedWall.sides?.A?.radiators, 'A', angle);
    snapRadiators(typedWall, typedWall.sides?.B?.radiators, 'B', angle);
    snapRadiators(typedWall, (typedWall as WallWithLegacyRadiators).radiators, 'A', angle);
  });

  relinkRoomWallReferences(snappedBuilding);

  return snappedBuilding;
};
